import {useCallback, useEffect, useMemo, useState} from 'react';

import {usePrevious} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  clearFocused,
  getSearchIdAttribute,
  isElementVisible,
  scrollIntoView,
  setFocusedAttribute,
} from '../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import type {SearchInConversationResponseModel} from '../../../../../models/api/searchInConversationMatchModel';
import {expandActivityViewerAsyncAction} from '../../../../../store/panels/viewer/activity/activityViewerActions';
import {jumpToActivityAsyncAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {useHighlightsCoordinator} from '../../../../common/highlighter/highlightsCoordinator';

type UseHighlightedMatchesProps = {
  panelId: ViewerPanelId;
  matchingActivityId?: number | null;
  onElementsEnd: (direction: 'start' | 'end') => void;
};

// @TODO: Merge this with useConversationSearchContextActivities at some point.
export const useHighlightedMatches = ({
  panelId,
  matchingActivityId,
  onElementsEnd,
}: UseHighlightedMatchesProps) => {
  const previousMatchingActivityId = usePrevious(matchingActivityId);
  const [highlightedElements, setHighlightedElements] = useState<ReadonlyArray<Element>>([]);
  const [matchIndex, setMatchIndex] = useState<number>(0);
  const previousMatchIndex = usePrevious(matchIndex);

  const matchingElementsForActivity = useMemo(
    () => highlightedElements.filter((e) => getSearchIdAttribute(e) === matchingActivityId?.toString()),
    [highlightedElements, matchingActivityId],
  );

  const highlightsCoordinator = useHighlightsCoordinator();

  const dispatch = useAppDispatch();

  const jumpToActivity = useCallback(
    async (activityId: number) => {
      clearFocused(matchingElementsForActivity);
      await dispatch(
        jumpToActivityAsyncAction(panelId, {id: activityId}, undefined, {
          shouldSkipGraceTimeout: true,
        }),
      );
      await dispatch(expandActivityViewerAsyncAction(panelId, activityId));
      if (highlightsCoordinator) {
        highlightsCoordinator.refresh();
      }
    },
    [dispatch, panelId, highlightsCoordinator, matchingElementsForActivity],
  );

  const onChangeMatchingActivityId = useCallback(
    async (activityId: number, elements: ReadonlyArray<Element>) => {
      await jumpToActivity(activityId);
      setFocusOnActivity(elements, activityId);
    },
    [jumpToActivity],
  );

  // If the matching activity changes, jump to it.
  useEffect(() => {
    if (!matchingActivityId || matchingActivityId === previousMatchingActivityId) {
      return;
    }
    onChangeMatchingActivityId(matchingActivityId, highlightedElements);
  }, [matchingActivityId, previousMatchingActivityId, highlightedElements, onChangeMatchingActivityId]);

  const onResults = useCallback(
    (highlights: ReadonlyArray<Element>) => {
      clearFocused(highlights);
      setHighlightedElements(highlights);
      const highlightsForActivity = highlights.filter(
        (e) => getSearchIdAttribute(e) === matchingActivityId?.toString(),
      );
      if (matchIndex !== previousMatchIndex) {
        if (highlightsForActivity[matchIndex]) {
          setFocusOnElement(highlightsForActivity, highlightsForActivity[matchIndex]);
        } else if (highlightsForActivity.length > 0) {
          setFocusOnElement(highlightsForActivity, highlightsForActivity[highlightsForActivity.length - 1]);
          setMatchIndex(highlightsForActivity.length - 1);
        }
      }
    },
    [setHighlightedElements, matchIndex, matchingActivityId, previousMatchIndex],
  );

  const goToNextMatch = useCallback(() => {
    if (!matchingActivityId) {
      return;
    }
    clearFocused(matchingElementsForActivity);
    let newVal = matchIndex + 1;
    if (newVal >= matchingElementsForActivity.length) {
      newVal = 0;
    }
    setMatchIndex(newVal);
    if (newVal === 0) {
      onElementsEnd('end');
      return;
    }

    if (matchingElementsForActivity[newVal]) {
      setFocusOnElement(matchingElementsForActivity, matchingElementsForActivity[newVal]);
    }
  }, [setMatchIndex, matchingElementsForActivity, matchingActivityId, matchIndex, onElementsEnd]);

  const goToPreviousMatch = useCallback(
    (activityMatches: SearchInConversationResponseModel['matchesInConversation']) => {
      if (!matchingActivityId) {
        return;
      }
      clearFocused(matchingElementsForActivity);
      let newIndex = matchIndex - 1;
      let matchingElementsForNewActivity = matchingElementsForActivity;
      // If we're at the first highlight, go to the last match of the previous highlight.
      if (newIndex < 0) {
        // Get the current activity index.
        const currentActivityMatchedIndex = activityMatches.findIndex((m) => m.id === matchingActivityId);
        // Get the previous activity index.
        const newActivityIdIndex =
          currentActivityMatchedIndex === 0 ? activityMatches.length - 1 : currentActivityMatchedIndex - 1;
        // Get the previous activity id.
        const newActivityId = activityMatches[newActivityIdIndex]?.id;
        // Get the elements for the previous activity.
        matchingElementsForNewActivity = highlightedElements.filter(
          (he) => getSearchIdAttribute(he) === newActivityId?.toString(),
        );
        // If there are no elements for the previous activity, it means its not loaded yet.
        if (matchingElementsForNewActivity.length === 0) {
          // Set the index to -1 to indicate that we're at the end of the previous activity.
          setMatchIndex(-1);
          // Call to load the previous activity.
          onElementsEnd('start');
          return;
        }
        // Set the new index to the last element of the previous activity.
        newIndex = matchingElementsForNewActivity.length - 1;
      }

      setMatchIndex(newIndex);
      if (matchingElementsForNewActivity[newIndex]) {
        setFocusOnElement(matchingElementsForNewActivity, matchingElementsForNewActivity[newIndex]);
      }

      if (newIndex === matchingElementsForNewActivity.length - 1) {
        onElementsEnd('start');
      }
    },
    [
      setMatchIndex,
      matchingActivityId,
      matchIndex,
      onElementsEnd,
      highlightedElements,
      matchingElementsForActivity,
    ],
  );

  return {
    onResults,
    goToNextMatch,
    goToPreviousMatch,
    matchIndex,
  };
};

const goToElement = (element: Element) => {
  if (!isElementVisible(element)) {
    scrollIntoView(element);
  }
};
const setFocusOnElement = (elements: ReadonlyArray<Element>, element: Element) => {
  clearFocused(elements);
  setFocusedAttribute(element);
  goToElement(element);
};

const setFocusOnActivity = (elements: ReadonlyArray<Element>, activityId: number) => {
  const matchingElement = elements.find((e) => getSearchIdAttribute(e) === activityId.toString());
  if (!matchingElement) {
    return;
  }
  setFocusOnElement(elements, matchingElement);
};
